/**
 * Validazione condivisa dei dati di contatto (email + telefono) e submit unico.
 *
 * FONTE UNICA lato frontend: usata dal form Contatti (pages/Contact.tsx), dal form
 * Send Request (chat/ContactWidget.tsx) e dal checkout (validazione telefono).
 * Applica le STESSE regole del backend (app/core/contact_validation.py):
 * plausibilità e formato, NON aggressiva. Nessuna verifica di esistenza reale.
 */

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_SEPARATORS = /[\s()\-. /]/g;

export type ContactSource = "contact_page" | "send_request_aside";

export interface ContactPayload {
  name: string;
  email: string;
  message: string;
  company?: string;
  phone?: string;
  subject?: string;
  source: ContactSource;
  locale: string;
}

export function normalizeEmail(raw: string): string {
  return (raw ?? "").trim().toLowerCase();
}

export function isValidEmail(raw: string): boolean {
  const e = normalizeEmail(raw);
  if (!e || e.length > 254) return false;
  if (e.includes(" ") || (e.match(/@/g) || []).length !== 1) return false;
  return EMAIL_RE.test(e);
}

export function normalizePhone(raw: string): string {
  const s = (raw ?? "").trim();
  if (!s) return "";
  const digits = s.replace(/\D/g, "");
  return s.startsWith("+") ? "+" + digits : digits;
}

/** Telefono plausibile: opzionale '+' + 6–15 cifre, tollerando spazi/parentesi/trattini/punti. */
export function isValidPhone(raw: string): boolean {
  const s = (raw ?? "").trim();
  if (!s || s.length > 32) return false;
  const core = s.replace(PHONE_SEPARATORS, "");
  return /^\+?\d{6,15}$/.test(core);
}

export interface ContactErrors {
  name?: boolean;
  email?: boolean;
  phone?: boolean;
  message?: boolean;
}

/** name/email/message obbligatori; company/phone opzionali (validati se presenti). */
export function validateContact(form: {
  name: string;
  email: string;
  phone?: string;
  message: string;
}): ContactErrors {
  const errors: ContactErrors = {};
  if (!form.name?.trim()) errors.name = true;
  if (!isValidEmail(form.email)) errors.email = true;
  if (form.phone && form.phone.trim() && !isValidPhone(form.phone)) errors.phone = true;
  if (!form.message?.trim()) errors.message = true;
  return errors;
}

import { analyticsIds } from "../analytics/tracker";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";

/** Submit unico verso POST /api/v1/shop/contact (stesso payload per entrambi i form). */
export async function submitContactRequest(payload: ContactPayload): Promise<void> {
  const res = await fetch(`${API_BASE}/api/v1/shop/contact`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: payload.name.trim(),
      email: normalizeEmail(payload.email),
      message: payload.message.trim(),
      company: payload.company?.trim() || undefined,
      phone: payload.phone?.trim() ? normalizePhone(payload.phone) : undefined,
      subject: payload.subject?.trim() || undefined,
      source: payload.source,
      locale: payload.locale,
      // Solo identificatori opachi: il contenuto del modulo non entra mai
      // nell'analytics, solo il fatto che una richiesta è arrivata e da dove
      // veniva chi l'ha inviata.
      ...analyticsIds(),
    }),
  });
  if (!res.ok) throw new Error("contact_submit_failed");
}
